import React, { useMemo, memo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useServices, useSubservices } from '../hooks/useAirtable';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ServiceDetailSidebar from '../components/ServiceDetailSidebar';
import ServicesBottomBar from '../components/ServicesBottomBar';
import ServiceDetailPage from './ServiceDetailPage';
import SubServiceDetailPage from './SubServiceDetailPage';

const MemoizedHeader = memo(Header);
const MemoizedFooter = memo(Footer);
const MemoizedSidebar = memo(ServiceDetailSidebar);
const MemoizedServicesBottomBar = memo(ServicesBottomBar);

/**
 * Layout ثابت للخدمات: الهيدر والقائمة الجانبية لا يختفيان عند التنقل، يتغير المحتوى فقط.
 */
export default function ServicesLayout({ onOpenChat }) {
  const params = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  const { lang, id } = params;
  const type = pathname.includes('/subservice/') ? 'subservice' : 'service';

  const activeLanguage = (lang === 'ar' || lang === 'en') ? lang : (currentLanguage || 'en');

  const { services } = useServices(true, activeLanguage);
  const { subservices } = useSubservices(null, true, activeLanguage);
  const memoizedServices = useMemo(() => services || [], [services]);
  const memoizedSubservices = useMemo(() => subservices || [], [subservices]);

  const currentService = useMemo(() => {
    if (!id || !memoizedServices.length) return null;
    if (type === 'service') {
      return memoizedServices.find(s => String(s.id) === String(id)) || null;
    }
    const sub = memoizedSubservices.find(s => String(s.id) === String(id));
    if (!sub) return null;
    const pid = sub.parent_service_id ?? sub.main_service_id ?? sub.service_id ?? sub.Subservices;
    return memoizedServices.find(s => String(s.id) === String(pid)) || null;
  }, [id, type, memoizedServices, memoizedSubservices]);

  const currentSubservice = useMemo(() => {
    if (type !== 'subservice' || !id || !memoizedSubservices.length) return null;
    return memoizedSubservices.find(s => String(s.id) === String(id)) || null;
  }, [type, id, memoizedSubservices]);

  const isRTL = activeLanguage === 'ar';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f9fafb',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <MemoizedHeader onOpenChat={onOpenChat} />

      <div style={{ height: '100px', width: '100%', flexShrink: 0 }} />

      <div
        className="services-layout-inner"
        style={{
          display: 'flex',
          flex: 1,
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 20px 40px',
          gap: '40px',
          alignItems: 'flex-start',
        }}
      >
        {/* قائمة جانبية ثابتة - ديسكتوب فقط */}
        <aside
          className="services-layout-sidebar"
          style={{
            width: '280px',
            minWidth: '280px',
            flexShrink: 0,
            position: 'sticky',
            top: '100px',
            alignSelf: 'flex-start',
          }}
        >
          <MemoizedSidebar
            services={memoizedServices}
            currentService={currentService}
            currentSubservice={currentSubservice}
            subservices={memoizedSubservices}
            language={activeLanguage}
            onServiceClick={(serviceId) => {
              navigate(`/${activeLanguage}/service/${serviceId}`, { replace: false });
              window.scrollTo({ top: 0, behavior: 'auto' });
            }}
            onSubserviceClick={(subserviceId) => {
              navigate(`/${activeLanguage}/subservice/${subserviceId}`, { replace: false });
              window.scrollTo({ top: 0, behavior: 'auto' });
            }}
          />
        </aside>

        {/* المحتوى فقط يتغير */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {type === 'subservice' ? (
            <SubServiceDetailPage onOpenChat={onOpenChat} contentOnly />
          ) : (
            <ServiceDetailPage onOpenChat={onOpenChat} contentOnly />
          )}
        </main>
      </div>

      <div style={{ marginTop: 'auto', paddingTop: '60px' }}>
        <MemoizedFooter />
      </div>

      {/* بار الخدمات السفلي - جوال فقط */}
      <div className="services-layout-bottom-bar" style={{ display: 'none' }}>
        <MemoizedServicesBottomBar
          services={memoizedServices}
          currentService={currentService}
          currentSubservice={currentSubservice}
          subservices={memoizedSubservices}
          language={activeLanguage}
          onServiceClick={(sid) => {
            navigate(`/${activeLanguage}/service/${sid}`, { replace: false });
            window.scrollTo({ top: 0, behavior: 'auto' });
          }}
          onSubserviceClick={(subId) => {
            navigate(`/${activeLanguage}/subservice/${subId}`, { replace: false });
            window.scrollTo({ top: 0, behavior: 'auto' });
          }}
        />
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .services-layout-sidebar { display: none !important; }
          .services-layout-bottom-bar { display: block !important; }
          /* المحتوى بعرض كامل + مساحة للبار السفلي */
          .services-layout-inner { flex-direction: column !important; align-items: stretch !important; padding-left: 16px !important; padding-right: 16px !important; padding-bottom: 120px !important; gap: 0 !important; }
          .services-layout-inner main { width: 100% !important; max-width: 100% !important; min-width: 0 !important; flex: 0 1 auto !important; }
        }
        @media (min-width: 1025px) {
          .services-layout-bottom-bar { display: none !important; }
        }
      `}</style>
    </div>
  );
}
